/**
 * Claude Agent SDK Adapter — implements SdkAdapter for @anthropic-ai/claude-agent-sdk
 *
 * Uses the `query()` function from the Claude Agent SDK to run Claude as a
 * stateful coding agent with tool execution (Read, Edit, Bash, etc.).
 *
 * Supports:
 * - send()          — start a new query session and return the result text
 * - followUp()      — resume the same session for output-format corrections
 * - sendExecution() — rich path using full contract context
 *
 * The SDK returns an AsyncGenerator of SDKMessage events. The adapter iterates
 * the stream, extracts the final result text from the SDKResultSuccess message,
 * and captures the session_id for followUp via `resume`.
 *
 * GuardrailHooks are mapped to the SDK's PreToolUse hook system.
 */

import type {
  SdkAdapter,
  AdapterSendOptions,
  AgentExecutionRequest,
  AgentProgressEvent,
  CacheConfig,
  CandidateAgent,
} from "../lib/task-runner.js";
import type { ObserverEmitFn, TokenUsage } from "../lib/observability-bridge.js";
import {
  emitLlmTurnOpen,
  emitLlmTurnClose,
  emitToolExecOpen,
  emitToolExecClose,
} from "../lib/observability-bridge.js";
import type { GuardrailHooks } from "../lib/guardrail-hooks.js";
import type { MemoryRef } from "../lib/memory-store.js";
import type { LibraryToolRegistry } from "../lib/library-tool-binding.js";
import { buildClaudeMcpServer, type McpObservabilityContext } from "../lib/library-tool-sdk.js";
import { ContextPackAdoptionTracker } from "../lib/context-pack-adoption.js";
import { ToolExecSpanRegistry } from "../lib/tool-exec-span-registry.js";

/**
 * Subset of the Claude Agent SDK `AgentDefinition` shape we populate.
 * Verified against node_modules/@anthropic-ai/claude-agent-sdk/sdk.d.ts:38
 * (`description`, `prompt`, optional `tools`, `model`).
 */
type ClaudeAgentDefinition = {
  description: string;
  prompt: string;
  tools?: string[];
  model?: string;
};

// ---------------------------------------------------------------------------
// Duck-typed SDK types (avoid hard compile-time dependency on zod v4)
// ---------------------------------------------------------------------------

type SDKResultSuccess = {
  type: "result";
  subtype: "success";
  result: string;
  session_id: string;
};

type SDKResultError = {
  type: "result";
  subtype: "error";
  error: string;
  session_id: string;
};

type SDKContentBlock = {
  type: string;
  name?: string;
  text?: string;
  input?: unknown;
};

type SDKBetaMessage = {
  content?: SDKContentBlock[];
};

type SDKMessage = {
  type: string;
  subtype?: string;
  result?: string;
  error?: string;
  session_id?: string;
  tool_name?: string;
  summary?: string;
  message?: SDKBetaMessage;
  usage?: {
    input_tokens?: number;
    output_tokens?: number;
    cache_read_input_tokens?: number;
    cache_creation_input_tokens?: number;
  };
};

type HookCallbackInput = {
  hook_event_name: string;
  tool_name: string;
  tool_input: unknown;
  [key: string]: unknown;
};

type HookCallbackOutput = {
  hookEventName: string;
  permissionDecision?: "allow" | "deny" | "ask";
  permissionDecisionReason?: string;
  additionalContext?: string;
};

type HookCallbackMatcher = {
  tool_name?: string;
  hooks: Array<(input: HookCallbackInput) => HookCallbackOutput | Promise<HookCallbackOutput>>;
};

type QueryOptions = Record<string, unknown>;

type QueryFn = (params: {
  prompt: string;
  options?: QueryOptions;
}) => AsyncGenerator<SDKMessage, void>;

// ---------------------------------------------------------------------------
// Guardrail hook bridge
// ---------------------------------------------------------------------------

function buildClaudeHooks(
  guardrails: GuardrailHooks,
): Partial<Record<string, HookCallbackMatcher[]>> {
  const preToolUseHook: HookCallbackMatcher = {
    hooks: [
      (input: HookCallbackInput): HookCallbackOutput => {
        const toolInput = input.tool_input as Record<string, unknown> | undefined;
        const toolName = input.tool_name;

        if (toolName === "Bash" || toolName === "Shell") {
          const command = (toolInput?.command ?? toolInput?.input) as string | undefined;
          if (command) {
            const result = guardrails.beforeShellExecution({ command });
            if (result.permission === "deny") {
              return {
                hookEventName: "PreToolUse",
                permissionDecision: "deny",
                permissionDecisionReason: result.user_message ?? result.agent_message,
              };
            }
            if (result.additionalContext) {
              return { hookEventName: "PreToolUse", additionalContext: result.additionalContext };
            }
          }
        }

        const filePath = (toolInput?.file_path ?? toolInput?.path) as string | undefined;
        if (filePath) {
          const pathResult = guardrails.preToolUse({
            tool_name: toolName,
            tool_input: { file_path: filePath },
          });
          if (pathResult.permission === "deny") {
            return {
              hookEventName: "PreToolUse",
              permissionDecision: "deny",
              permissionDecisionReason: pathResult.user_message ?? pathResult.agent_message,
            };
          }
          if (pathResult.additionalContext) {
            return { hookEventName: "PreToolUse", additionalContext: pathResult.additionalContext };
          }
        }

        return { hookEventName: "PreToolUse" };
      },
    ],
  };

  return { PreToolUse: [preToolUseHook] };
}

// ---------------------------------------------------------------------------
// Input summary truncation
// ---------------------------------------------------------------------------

function truncateInputSummary(input: unknown, maxLen = 80): string {
  if (input == null) return "";
  let str: string;
  if (typeof input === "string") {
    str = input;
  } else {
    try { str = JSON.stringify(input); } catch { str = String(input); }
  }
  return str.length > maxLen ? str.slice(0, maxLen - 3) + "..." : str;
}

// ---------------------------------------------------------------------------
// Config
// ---------------------------------------------------------------------------

export interface ClaudeAgentSdkAdapterConfig {
  /** Working directory for the agent. Defaults to process.cwd(). */
  cwd?: string;
  /** Model identifier (e.g. "claude-sonnet-4-20250514"). Uses SDK default if omitted. */
  model?: string;
  /**
   * Available tools. Defaults to read-only or read-write set based on AdapterSendOptions.
   * Set to `{ type: 'preset', preset: 'claude_code' }` for all Claude Code tools.
   */
  tools?: string[] | { type: "preset"; preset: "claude_code" };
  /** Permission mode. Defaults to "bypassPermissions" for automated workflows. */
  permissionMode?: "default" | "acceptEdits" | "bypassPermissions" | "plan";
  /** Max turns before stopping. */
  maxTurns?: number;
  /** Runtime guardrail hooks enforced via PreToolUse SDK hooks. */
  guardrailHooks?: GuardrailHooks;
  /** Prompt caching configuration. Enabled by default. */
  cacheConfig?: CacheConfig;
}

// ---------------------------------------------------------------------------
// Adapter
// ---------------------------------------------------------------------------

export class ClaudeAgentSdkAdapter implements SdkAdapter {
  private cwd: string;
  private model: string | undefined;
  private tools: string[] | { type: "preset"; preset: "claude_code" } | undefined;
  private permissionMode: string;
  private maxTurns: number | undefined;
  private guardrailHooks: GuardrailHooks | undefined;
  private cacheConfig: CacheConfig;
  private lastSessionId: string | null = null;
  private lastMemoryRef: MemoryRef | null = null;
  private lastTokenUsage: TokenUsage | null = null;
  private queryFn: QueryFn | null = null;

  constructor(config: ClaudeAgentSdkAdapterConfig = {}) {
    this.cwd = config.cwd ?? process.cwd();
    this.model = config.model;
    this.tools = config.tools;
    this.permissionMode = config.permissionMode ?? "bypassPermissions";
    this.maxTurns = config.maxTurns;
    this.guardrailHooks = config.guardrailHooks;
    this.cacheConfig = config.cacheConfig ?? { enabled: true };
  }

  // -------------------------------------------------------------------------
  // Internal helpers
  // -------------------------------------------------------------------------

  private async resolveQueryFn(): Promise<QueryFn> {
    if (this.queryFn) return this.queryFn;

    const sdk = (await import("@anthropic-ai/claude-agent-sdk")) as {
      query: QueryFn;
    };
    this.queryFn = sdk.query;
    return this.queryFn;
  }

  private buildOptions(
    readonly: boolean,
    resume?: string,
    systemPrompt?: string[],
    agents?: CandidateAgent[],
    allowDynamicWorkflow?: boolean,
  ): QueryOptions {
    const opts: QueryOptions = {
      cwd: this.cwd,
      permissionMode: this.permissionMode,
      allowDangerouslySkipPermissions: this.permissionMode === "bypassPermissions",
      // SDK isolation: do not load ambient .claude/ settings, skills, or agents
      // from disk. The runtime injects everything in-code, so disk discovery
      // would break component isolation and reproducibility.
      settingSources: [],
    };

    if (this.model) opts.model = this.model;
    if (this.maxTurns) opts.maxTurns = this.maxTurns;

    let toolList: string[] | { type: "preset"; preset: "claude_code" };
    if (this.tools) {
      toolList = Array.isArray(this.tools) ? [...this.tools] : this.tools;
    } else {
      toolList = readonly
        ? ["Read", "Glob", "Grep", "Bash"]
        : ["Read", "Glob", "Grep", "Edit", "Write", "Bash"];
    }

    // When candidate sub-agents are registered (model B), the entry agent needs
    // the sub-agent delegation tool to invoke them. The restricted tool list
    // above omits it, so add it explicitly — otherwise the LLM has no way to
    // delegate. The SDK names this tool "Task"/"Agent" (docs use both; tool_use
    // events surface it as "Agent"), so allow both for robustness across
    // SDK versions. (A "preset" tools config already includes it.)
    if (agents && agents.length > 0 && Array.isArray(toolList)) {
      for (const t of ["Task", "Agent"]) {
        if (!toolList.includes(t)) toolList.push(t);
      }
    }

    if (allowDynamicWorkflow && Array.isArray(toolList)) {
      if (!toolList.includes("Workflow")) toolList.push("Workflow");
    }
    opts.tools = toolList;

    if (resume) opts.resume = resume;

    if (systemPrompt && this.cacheConfig.enabled !== false) {
      opts.systemPrompt = systemPrompt;
    }

    // Register candidate sub-agents so the LLM can delegate via the Agent/Task
    // tool (model B, LLM-internal routing). Keyed by candidate name.
    if (agents && agents.length > 0) {
      const record: Record<string, ClaudeAgentDefinition> = {};
      for (const a of agents) {
        record[a.name] = {
          description: a.description,
          prompt: a.prompt,
          ...(a.tools ? { tools: a.tools } : {}),
          ...(a.model ? { model: a.model } : {}),
        };
      }
      opts.agents = record;
    }

    if (this.guardrailHooks) {
      opts.hooks = buildClaudeHooks(this.guardrailHooks);
    }

    return opts;
  }

  private async attachLibraryTools(
    opts: QueryOptions,
    libraryTools?: LibraryToolRegistry,
    observability?: McpObservabilityContext,
  ): Promise<void> {
    if (!libraryTools || libraryTools.getAll().length === 0) return;

    try {
      const server = await buildClaudeMcpServer(libraryTools, observability);
      if (server) {
        const existing = (opts.mcpServers ?? []) as unknown[];
        opts.mcpServers = [...existing, server];
      }
    } catch {
      // SDK functions unavailable; library tools silently skipped for Claude
    }
  }

  private createMcpObservability(
    observer?: ObserverEmitFn,
    sessionId?: string,
  ): McpObservabilityContext | undefined {
    if (!observer) return undefined;
    return {
      observer,
      sessionId,
      spanRegistry: new ToolExecSpanRegistry(),
      adoptionTracker: new ContextPackAdoptionTracker(),
    };
  }

  private async runQuery(
    prompt: string,
    options: QueryOptions,
    onProgress?: (event: AgentProgressEvent) => void,
    observer?: ObserverEmitFn,
    parentSpanId?: string,
    agentSessionId?: string,
    mcpObservability?: McpObservabilityContext,
  ): Promise<{ text: string; sessionId: string; tokenUsage?: TokenUsage }> {
    const queryFn = await this.resolveQueryFn();
    const stream = queryFn({ prompt, options });

    let resultText = "";
    let sessionId = "";

    // Token usage accumulation across all messages in this query
    let totalInputTokens = 0;
    let totalOutputTokens = 0;
    let totalCacheReadTokens = 0;
    let totalCacheCreationTokens = 0;
    let hasUsageData = false;

    // Inner turn-level instrumentation state
    let turnNumber = 0;
    let currentTurnSpanId: string | undefined;
    let currentTurnStart = 0;
    let toolCallsInTurn = 0;
    let turnInputTokens = 0;
    let turnOutputTokens = 0;
    let turnCacheHitTokens = 0;
    const openToolSpans: Array<{ spanId: string; toolName: string; startMs: number }> = [];

    const closePendingToolSpans = (): void => {
      for (const ts of openToolSpans) {
        if (mcpObservability?.spanRegistry?.isClosed(ts.spanId)) continue;
        emitToolExecClose(observer, ts.spanId, ts.toolName, Date.now() - ts.startMs, agentSessionId);
      }
      openToolSpans.length = 0;
    };

    const closeCurrentTurn = (): void => {
      if (currentTurnSpanId) {
        emitLlmTurnClose(observer, currentTurnSpanId, turnNumber, Date.now() - currentTurnStart, {
          inputTokens: turnInputTokens || undefined,
          outputTokens: turnOutputTokens || undefined,
          cacheHitTokens: turnCacheHitTokens || undefined,
          toolCallsCount: toolCallsInTurn || undefined,
        }, agentSessionId);
        currentTurnSpanId = undefined;
      }
    };

    const captureUsage = (usage: SDKMessage["usage"], perTurn = true): void => {
      if (!usage) return;
      if (typeof usage.input_tokens === "number") {
        totalInputTokens += usage.input_tokens;
        if (perTurn) turnInputTokens += usage.input_tokens;
        hasUsageData = true;
      }
      if (typeof usage.output_tokens === "number") {
        totalOutputTokens += usage.output_tokens;
        if (perTurn) turnOutputTokens += usage.output_tokens;
        hasUsageData = true;
      }
      if (typeof usage.cache_read_input_tokens === "number") {
        totalCacheReadTokens += usage.cache_read_input_tokens;
        if (perTurn) turnCacheHitTokens += usage.cache_read_input_tokens;
        hasUsageData = true;
      }
      if (typeof usage.cache_creation_input_tokens === "number") {
        totalCacheCreationTokens += usage.cache_creation_input_tokens;
        hasUsageData = true;
      }
    };

    for await (const message of stream) {
      if (message.session_id) sessionId = message.session_id;

      if (message.type === "result") {
        if (message.usage) {
          if (turnInputTokens === 0 && typeof message.usage.input_tokens === "number") {
            turnInputTokens = message.usage.input_tokens;
          }
          if (turnOutputTokens === 0 && typeof message.usage.output_tokens === "number") {
            turnOutputTokens = message.usage.output_tokens;
          }
          if (turnCacheHitTokens === 0 && typeof message.usage.cache_read_input_tokens === "number") {
            turnCacheHitTokens = message.usage.cache_read_input_tokens;
          }
        }
        captureUsage(message.usage, false);
        closePendingToolSpans();
        closeCurrentTurn();
        if (message.subtype === "success") {
          resultText = (message as SDKResultSuccess).result;
        } else if (message.subtype === "error") {
          throw new Error(`Claude Agent SDK error: ${(message as SDKResultError).error}`);
        }
      } else if (message.type === "assistant") {
        closePendingToolSpans();
        closeCurrentTurn();

        turnNumber++;
        toolCallsInTurn = 0;
        turnInputTokens = 0;
        turnOutputTokens = 0;
        turnCacheHitTokens = 0;
        currentTurnStart = Date.now();
        currentTurnSpanId = emitLlmTurnOpen(observer, parentSpanId, turnNumber, this.model, undefined, agentSessionId);

        captureUsage(message.usage);

        const content = message.message?.content;
        if (content) {
          for (const block of content) {
            if (block.type === "tool_use" && block.name) {
              toolCallsInTurn++;
              const summary = truncateInputSummary(block.input);
              const adoption = mcpObservability?.adoptionTracker?.evaluate(
                agentSessionId,
                block.name,
                block.input,
              );
              const openOpts = adoption
                ? {
                    sessionId: agentSessionId,
                    extraAttributes: {
                      "tool.adoption.kind": adoption.kind,
                      "tool.context_pack_id": adoption.contextPackId,
                    },
                    links: adoption.links,
                  }
                : agentSessionId;
              const toolSpanId = emitToolExecOpen(
                observer,
                currentTurnSpanId,
                block.name,
                summary,
                turnNumber,
                openOpts,
              );
              if (toolSpanId) {
                const entry = { spanId: toolSpanId, toolName: block.name, startMs: Date.now() };
                openToolSpans.push(entry);
                mcpObservability?.spanRegistry?.register(entry);
              }
            }
          }
        }

        if (onProgress) {
          emitProgressFromSdkMessage(message, onProgress, sessionId);
        }
      } else {
        captureUsage(message.usage);
        if (onProgress) {
          emitProgressFromSdkMessage(message, onProgress, sessionId);
        }
      }
    }

    closePendingToolSpans();
    closeCurrentTurn();

    const tokenUsage: TokenUsage | undefined = hasUsageData
      ? {
          inputTokens: totalInputTokens,
          outputTokens: totalOutputTokens,
          totalTokens: totalInputTokens + totalOutputTokens,
          ...(totalCacheReadTokens > 0 ? { cacheReadTokens: totalCacheReadTokens } : {}),
          ...(totalCacheCreationTokens > 0 ? { cacheCreationTokens: totalCacheCreationTokens } : {}),
        }
      : undefined;

    return { text: resultText, sessionId, tokenUsage };
  }

  // -------------------------------------------------------------------------
  // SdkAdapter interface
  // -------------------------------------------------------------------------

  async send(
    prompt: string,
    options: AdapterSendOptions,
  ): Promise<string> {
    this.lastSessionId = null;
    this.lastMemoryRef = null;
    this.lastTokenUsage = null;

    const split = options.splitPrompt;
    const systemPrompt = split?.system;
    const userPrompt = split ? split.user : prompt;
    const opts = this.buildOptions(options.readonly, undefined, systemPrompt, options.agents, options.allowDynamicWorkflow);
    const mcpObservability = this.createMcpObservability(options.observer, options.sessionId);
    await this.attachLibraryTools(opts, options.libraryTools, mcpObservability);
    const { text, sessionId, tokenUsage } = await this.runQuery(
      userPrompt,
      opts,
      options.onProgress,
      options.observer,
      options.parentSpanId,
      options.sessionId,
      mcpObservability,
    );

    this.lastSessionId = sessionId;
    this.lastTokenUsage = tokenUsage ?? null;
    this.lastMemoryRef = {
      id: sessionId,
      provider: "claude-agent-sdk",
      compat: "claude-agent-sdk@0.2",
      created_at: new Date().toISOString(),
    };
    return text;
  }

  async followUp(message: string): Promise<string> {
    if (!this.lastSessionId) {
      throw new Error("followUp() called before send() — no active session");
    }
    const opts = this.buildOptions(false, this.lastSessionId);
    const { text, sessionId, tokenUsage } = await this.runQuery(message, opts);
    this.lastSessionId = sessionId;
    if (tokenUsage) {
      this.lastTokenUsage = this.lastTokenUsage
        ? {
            inputTokens: this.lastTokenUsage.inputTokens + tokenUsage.inputTokens,
            outputTokens: this.lastTokenUsage.outputTokens + tokenUsage.outputTokens,
            totalTokens: (this.lastTokenUsage.totalTokens ?? 0) + (tokenUsage.totalTokens ?? 0),
            cacheReadTokens: (this.lastTokenUsage.cacheReadTokens ?? 0) + (tokenUsage.cacheReadTokens ?? 0) || undefined,
            cacheCreationTokens: (this.lastTokenUsage.cacheCreationTokens ?? 0) + (tokenUsage.cacheCreationTokens ?? 0) || undefined,
          }
        : tokenUsage;
    }
    return text;
  }

  async sendExecution(request: AgentExecutionRequest): Promise<string> {
    this.lastSessionId = null;
    this.lastMemoryRef = null;
    this.lastTokenUsage = null;

    const resume = request.memoryRef?.id;
    const split = request.splitPrompt;
    const systemPrompt = split?.system;
    const userPrompt = split ? split.user : request.prompt;
    const agents = request.options.agents ?? request.agents;
    const opts = this.buildOptions(request.options.readonly, resume, systemPrompt, agents, request.options.allowDynamicWorkflow);
    const mcpObservability = this.createMcpObservability(
      request.options.observer,
      request.options.sessionId,
    );
    await this.attachLibraryTools(opts, request.options.libraryTools, mcpObservability);
    const { text, sessionId, tokenUsage } = await this.runQuery(
      userPrompt,
      opts,
      request.options.onProgress,
      request.options.observer,
      request.options.parentSpanId,
      request.options.sessionId,
      mcpObservability,
    );

    this.lastSessionId = sessionId;
    this.lastTokenUsage = tokenUsage ?? null;
    this.lastMemoryRef = {
      id: sessionId,
      provider: "claude-agent-sdk",
      compat: "claude-agent-sdk@0.2",
      created_at: new Date().toISOString(),
      parent_run_id: request.memoryRef?.id,
    };
    return text;
  }

  getLastMemoryRef(): MemoryRef | null {
    return this.lastMemoryRef;
  }

  getLastTokenUsage(): TokenUsage | null {
    return this.lastTokenUsage;
  }

  isCompatible(compat: string): boolean {
    return compat.startsWith("claude-agent-sdk@");
  }

  // -------------------------------------------------------------------------
  // Test support
  // -------------------------------------------------------------------------

  /**
   * Inject a custom query function for testing.
   * Bypasses the dynamic import of @anthropic-ai/claude-agent-sdk.
   */
  static withQueryFn(
    queryFn: QueryFn,
    config?: ClaudeAgentSdkAdapterConfig,
  ): ClaudeAgentSdkAdapter {
    const adapter = new ClaudeAgentSdkAdapter(config);
    adapter.queryFn = queryFn;
    return adapter;
  }
}

// ---------------------------------------------------------------------------
// Progress event mapping
// ---------------------------------------------------------------------------

function emitProgressFromSdkMessage(
  message: SDKMessage,
  onProgress: (event: AgentProgressEvent) => void,
  sessionId?: string,
): void {
  const sid = message.session_id ?? sessionId;
  switch (message.type) {
    case "assistant": {
      const content = message.message?.content;
      if (!content) break;
      for (const block of content) {
        if (block.type === "tool_use" && block.name) {
          const input = block.input && typeof block.input === "object"
            ? block.input as Record<string, unknown>
            : undefined;
          onProgress({ type: "tool_use", tool_name: block.name, input, session_id: sid });
        } else if (block.type === "text" && block.text) {
          onProgress({ type: "text", message: block.text, session_id: sid });
        }
      }
      break;
    }
    case "tool_progress":
      if (message.tool_name) {
        onProgress({ type: "tool_use", tool_name: message.tool_name, session_id: sid });
      }
      break;
    case "tool_use_summary":
      if (message.summary) {
        onProgress({ type: "status", message: message.summary, session_id: sid });
      }
      break;
    default:
      break;
  }
}
